import React, { useState, useRef, useEffect } from "react";
import { observer } from "mobx-react";
import copilotStore from "Stores/copilotStore";
import copilotDrawerStore from "Stores/copilotDrawerStore";
import { AWAIT } from "../agent/agentContract";
import { suggestPrompts } from "../agent/promptSuggestions";
import SparkleIcon from "Components/common/SparkleIcon";
import { voiceEnabled, voiceSupported, listen, startVoiceSession, stopVoiceSession, voiceSessionOn, attachVoiceUi, detachVoiceUi, speakFiller } from "../agent/voice";
import CopilotSettingsPopup from "./CopilotSettingsPopup";
import { trackDictation, trackVoiceStart, trackVoiceEnd, trackCopilotMessage } from "../agent/copilotAnalytics";
import VoiceDictationBar from "./VoiceDictationBar";
import VoiceConversationOverlay from "./VoiceConversationOverlay";
import VoiceModeIntro from "./VoiceModeIntro";
import { getPrefs } from "../agent/copilotPrefs";

const MAX_LENGTH = 2000;
const DEFAULT_PLACEHOLDER =
  "Ask Copilot to find companies, build prospect lists, or export to your CRM…";

// Contextual ghost text: the agent can send an explicit hint with turn_end;
// otherwise fall back to a generic nudge for the awaited interaction type.
const AWAIT_PLACEHOLDERS = {
  [AWAIT.QUICK_REPLY]: "Pick an option above, or type your answer…",
  [AWAIT.CTA]: "Choose an action above, or tell me what to do next…",
  [AWAIT.CONFIRMATION]: "Confirm above, or type yes / no…",
  [AWAIT.ACTION_RESULT]: "Finish the step above and I'll continue…",
  [AWAIT.QUESTION_FORM]: "Answer the questions above, or just type…",
  [AWAIT.SELLER_PROFILE_PREVIEW]: "Review and approve the profile above, or just type…",
  [AWAIT.WEBSITE_INPUT]: "Enter your website above and hit Research, or just type…"
};

// sendLabel: the hero renders a labelled "✦ Ask copilot" pill instead of the
// arrow; rows: the hero box is two lines tall so it reads as a prompt, not a
// search field.
const Composer = observer(({ placeholder = DEFAULT_PLACEHOLDER, sendLabel = "", rows = 1 }) => {
  const [value, setValue] = useState("");
  const [typeaheadIndex, setTypeaheadIndex] = useState(-1);
  const [typeaheadOpen, setTypeaheadOpen] = useState(false);
  // Suggestions are a nudge, not a step the user owes us — dismissing hides them
  // until the next turn produces a different set.
  const textareaRef = useRef(null);
  const wasDisabledRef = useRef(false);
  const typeahead = typeaheadOpen
    ? suggestPrompts(value, copilotStore.messages.length > 0)
    : [];

  // Hero prompt rows prefill the box through the store; user edits and sends.
  useEffect(() => {
    if (copilotStore.composerDraft && !value.trim()) {
      setValue(copilotStore.composerDraft);
      copilotStore.setComposerDraft("");
      setTypeaheadOpen(false);
      if (textareaRef.current) textareaRef.current.focus();
    }
  }, [copilotStore.composerDraft]);
  // Only a real disconnect disables the box now — an in-flight turn no longer
  // does, so focus never has a reason to leave the composer (like claude.ai:
  // sending while the assistant is responding queues the message instead).
  //
  // "Not connected yet" is not a disconnect: the drawer deliberately defers
  // connecting until the first message, so a never-connected composer must stay
  // usable or there is no way to send that first message. sendUserMessage
  // connects on demand.
  const disabled = copilotStore.connected === false && copilotStore.messages.length > 0;

  // Auto-grow the textarea up to the SCSS max-height cap. Empty box: no
  // inline height at all, so `rows` decides (a scrollHeight measured while
  // hidden or before fonts settle is wrong and used to pin it to one line).
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "";
    if (value) el.style.height = `${el.scrollHeight}px`;
    el.style.overflowY = el.scrollHeight > el.clientHeight ? "auto" : "hidden";
  }, [value]);

  // Safety net for the one remaining disable path (disconnect/reconnect):
  // a disabled textarea still gets its focus dropped by the browser, so
  // restore it once the box is enabled again.
  useEffect(() => {
    if (wasDisabledRef.current && !disabled && textareaRef.current) {
      textareaRef.current.focus();
    }
    wasDisabledRef.current = disabled;
  }, [disabled]);

  const handleSend = () => {
    const text = value.trim();
    if (!text || disabled) return;
    setValue("");
    setTypeaheadOpen(false);
    setTypeaheadIndex(-1);
    copilotStore.sendUserMessage(text);
    trackCopilotMessage(surface, "text");
  };

  // Two voice modes. Dictate: press the mic, say one thing, it sends as text
  // and the reply is text. Talk: a headset toggle keeps the mic on, each
  // utterance sends itself, the reply is read aloud, then it listens again.
  const [voiceState, setVoiceState] = useState(voiceSessionOn() ? "listening" : "off");
  const [dictating, setDictating] = useState(false);
  const [voiceSettingsOpen, setVoiceSettingsOpen] = useState(false);
  const [introOpen, setIntroOpen] = useState(false);
  // Talk-mode transcript lives in the waveform, never in the textarea: the
  // same words in two places reads as a bug and blocks typing mid-session.
  const [heard, setHeard] = useState("");
  // Which surface this composer belongs to, for adoption reporting.
  const surface = copilotDrawerStore.open ? "drawer" : "page";
  const voiceRun = useRef({ startedAt: 0, turns: 0 });
  // Reported once per conversation, whoever ended it: the Stop button, a
  // spoken "stop", or the orchestrator judging a sign-off.
  const endVoiceRun = endedBy => {
    const run = voiceRun.current;
    if (!run.startedAt) return;
    voiceRun.current = { startedAt: 0, turns: 0 };
    trackVoiceEnd({ surface, voice: getPrefs().voice, endedBy, startedAt: run.startedAt, turns: run.turns });
  };
  const voiceSettingsBtnRef = useRef(null);
  const stopDictation = useRef(null);
  const showMic = voiceEnabled() && voiceSupported();
  // Dictation buffers until the user decides. onFinal used to send on its
  // own, which meant a pause mid-thought became a message.
  const dictateBuffer = useRef("");
  const listenForDictation = () => {
    stopDictation.current = listen({
      onInterim: text => setHeard((dictateBuffer.current + " " + text).trim()),
      onFinal: text => {
        dictateBuffer.current = (dictateBuffer.current + " " + text).trim();
        setHeard(dictateBuffer.current);
      },
      onEnd: () => {
        stopDictation.current = null;
        // Recognition stops at every pause; keep the mic open until the user
        // sends or discards.
        if (dictatingRef.current) setTimeout(() => { if (dictatingRef.current) listenForDictation(); }, 120);
      }
    });
  };
  const dictatingRef = useRef(false);
  const endDictation = () => {
    dictatingRef.current = false;
    if (stopDictation.current) stopDictation.current();
    stopDictation.current = null;
    dictateBuffer.current = "";
    setHeard("");
    setDictating(false);
  };
  const sendDictation = () => {
    const text = (heard || dictateBuffer.current || "").trim();
    endDictation();
    if (!text) return;
    copilotStore.sendUserMessage(text, { voice: true });
    trackCopilotMessage(surface, "voice");
  };
  const toggleDictate = () => {
    if (dictatingRef.current) {
      endDictation();
      return;
    }
    if (voiceSessionOn()) toggleTalk();
    dictatingRef.current = true;
    dictateBuffer.current = "";
    setHeard("");
    setDictating(true);
    trackDictation(surface);
    listenForDictation();
  };

  const INTRO_SEEN = "sg_copilot_voice_intro";
  const askThenTalk = () => {
    if (voiceSessionOn()) {
      toggleTalk();
      return;
    }
    let seen = false;
    try {
      seen = localStorage.getItem(INTRO_SEEN) === "1";
    } catch (e) {
      seen = false;
    }
    if (seen) toggleTalk();
    else setIntroOpen(true);
  };
  const startFromIntro = () => {
    try {
      localStorage.setItem(INTRO_SEEN, "1");
    } catch (e) {
      /* private mode: they will see it again */
    }
    setIntroOpen(false);
    toggleTalk();
  };

  const toggleTalk = () => {
    if (voiceSessionOn()) {
      stopVoiceSession("user"); // reports through onState below
      setVoiceState("off");
      return;
    }
    if (stopDictation.current) stopDictation.current();
    startVoiceSession({
      onInterim: text => setHeard(text),
      onSend: text => {
        if (!text) return;
        setHeard("");
        voiceRun.current.turns += 1;
        // Ticks an open choice card when that is plainly what was said;
        // otherwise sends as a message.
        copilotStore.sendVoiceMessage(text);
        trackCopilotMessage(surface, "voice");
        // Answer the moment they stop talking, not when the server does.
        speakFiller(text);
      },
      onState: (state, endedBy) => {
        setVoiceState(state);
        if (state === "off") endVoiceRun(endedBy || "user");
      },
      // Talking over a reply that is still being generated stops it too.
      onInterrupt: () => copilotStore.interrupt()
    });
    // Take ownership of the hooks we just installed, so this composer's own
    // unmount is the only thing that can release them.
    claimVoiceUi();
    voiceRun.current = { startedAt: Date.now(), turns: 0 };
    trackVoiceStart(surface, getPrefs().voice);
  };
  // Talk mode outlives this composer: moving between the full page and the
  // drawer swaps composers, and the mic should stay on through it. Adopt a
  // running session on mount, hand it back on unmount; only dictation stops.
  // Voice callbacks outlive a render but must never outlive the mount: a
  // setState after unmount is what React reports as "Should have a queue".
  // The token says which composer owns the session's hooks, so the outgoing
  // one cannot wipe the incoming one's (React mounts before it unmounts).
  const live = useRef(true);
  const uiToken = useRef(null);
  const claimVoiceUi = () => {
    uiToken.current = attachVoiceUi({
      onInterim: text => { if (live.current) setHeard(text); },
      onState: (state, endedBy) => {
        if (!live.current) return;
        setVoiceState(state);
        if (state === "off") endVoiceRun(endedBy || "user");
      }
    });
  };
  useEffect(() => {
    live.current = true;
    claimVoiceUi();
    return () => {
      live.current = false;
      if (uiToken.current) detachVoiceUi(uiToken.current);
      if (stopDictation.current) stopDictation.current();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const listening = voiceState === "listening";

  const pickTypeahead = prompt => {
    setValue(prompt);
    setTypeaheadOpen(false);
    setTypeaheadIndex(-1);
    if (textareaRef.current) textareaRef.current.focus();
  };

  const handleKeyDown = event => {
    if (typeahead.length) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setTypeaheadIndex((typeaheadIndex + 1) % typeahead.length);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setTypeaheadIndex(
          (typeaheadIndex - 1 + typeahead.length) % typeahead.length
        );
        return;
      }
      if (event.key === "Escape") {
        setTypeaheadOpen(false);
        setTypeaheadIndex(-1);
        return;
      }
      if (event.key === "Enter" && !event.shiftKey && typeaheadIndex >= 0) {
        event.preventDefault();
        pickTypeahead(typeahead[typeaheadIndex]);
        return;
      }
    }
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="copilotComposer">
      {/* Two different things, deliberately: a bar you operate, and a
          presence you talk to. */}
      {introOpen && <VoiceModeIntro onStart={startFromIntro} onCancel={() => setIntroOpen(false)} />}
      {voiceSessionOn() && !voiceSettingsOpen && (
        <VoiceConversationOverlay
          state={copilotStore.streaming && voiceState !== "speaking" ? "thinking" : voiceState}
          transcript={heard}
          onEnd={toggleTalk}
        />
      )}
      <VoiceDictationBar
        active={dictating && !voiceSessionOn()}
        transcript={heard}
        onSend={sendDictation}
        onCancel={endDictation}
      />
      <div className="copilotComposer__inputWrap">
        {typeahead.length > 0 && (
          <div className="copilotComposer__typeahead">
            {typeahead.map((prompt, index) => (
              <button
                type="button"
                key={prompt}
                className={`copilotComposer__typeaheadItem ${
                  index === typeaheadIndex ? "active" : ""
                }`}
                onMouseDown={event => {
                  // mousedown, not click: fires before the textarea blurs
                  event.preventDefault();
                  pickTypeahead(prompt);
                }}
              >
                <i className="material-icons-outlined">search</i>
                <span>{prompt}</span>
              </button>
            ))}
          </div>
        )}
        <textarea
          ref={textareaRef}
          rows={rows}
          placeholder={
            copilotStore.awaitingHint ||
            AWAIT_PLACEHOLDERS[copilotStore.awaiting] ||
            placeholder
          }
          value={value}
          maxLength={MAX_LENGTH}
          onChange={event => {
            setValue(event.target.value);
            setTypeaheadOpen(true);
            setTypeaheadIndex(-1);
          }}
          onBlur={() => setTypeaheadOpen(false)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />
        {showMic && (
          <>
            <button
              type="button"
              className={`copilotComposer__micBtn${dictating ? " is-listening" : ""}`}
              onClick={toggleDictate}
              title={dictating ? "Stop" : "Dictate a message"}
              aria-pressed={dictating}
            >
              <span className="material-icons-round">{dictating ? "mic" : "mic_none"}</span>
            </button>
            <button
              type="button"
              className={`copilotComposer__talkBtn${voiceSessionOn() ? " is-on" : ""}${voiceState === "speaking" ? " is-speaking" : ""}`}
              onClick={askThenTalk}
              title={voiceSessionOn() ? "End conversation" : "Talk to Copilot"}
              aria-pressed={voiceSessionOn()}
            >
              <span className="copilotComposer__talkOrb" aria-hidden="true" />
            </button>
            <button
              type="button"
              ref={voiceSettingsBtnRef}
              className={`copilotComposer__micBtn copilotComposer__voiceSettingsBtn copilotPage__settingsBtn${voiceSettingsOpen ? " is-open" : ""}`}
              onClick={() => setVoiceSettingsOpen(open => !open)}
              title="Voice settings"
              aria-expanded={voiceSettingsOpen}
            >
              <span className="material-icons-round">tune</span>
            </button>
            {voiceSettingsOpen && (
              <CopilotSettingsPopup
                anchorRef={voiceSettingsBtnRef}
                onClose={() => setVoiceSettingsOpen(false)}
              />
            )}
          </>
        )}
        {copilotStore.streaming ? (
          <button
            type="button"
            className="copilotComposer__sendBtn copilotComposer__sendBtn--stop"
            onClick={copilotStore.interrupt}
            title="Stop"
          >
            <span className="material-icons-round">stop</span>
          </button>
        ) : (
          <button
            type="button"
            className={`copilotComposer__sendBtn${sendLabel ? " copilotComposer__sendBtn--labeled" : ""}`}
            onClick={handleSend}
            disabled={!value.trim() || disabled}
          >
            {sendLabel ? (
              <>
                <SparkleIcon />
                {sendLabel}
              </>
            ) : (
              <span className="material-icons-round">arrow_upward</span>
            )}
          </button>
        )}
      </div>
      <div className="copilotComposer__hint">
        {copilotStore.streaming
          ? "Copilot is responding… click stop to interrupt, or send your next message and it'll queue"
          : "Enter to send · Shift+Enter for a new line"}
      </div>
    </div>
  );
});

export default Composer;
